import React from 'react'
import { toMoney } from '@mobi/utils/money'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { spacing } from '@mobi/component-library/Foundations'
import { BetCard } from '@mobi/betslip/Components/Common/BetCard'
import type { BetLoopKambiShareCoupon } from '@core/Areas/BetLoop/types'
import { BetCardTitle } from '@mobi/betslip/Components/Common/BetCardTitle'
import { formatKambiCurrencyToDollarDecimal } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import { getEventLinkForKambiClient } from '@core/Areas/KambiClient/helpers/navigation'
import { SportsIcon } from '@mobi/sports-offering/Components/Common/SportsIcon'
import { TeamsIcon } from '@mobi/sports-offering/Components/Common/TeamsIcon'
import { KambiHistoryCouponDisplayContext } from '@core/Areas/KambiSports/Player/Components/BetHistory/context'
import { CountdownStatus } from '../Common/CountdownStatus'
import { LegStatusIcon } from '../Common/LegStatusIcon'
import { LegsProgressStatus } from '../Common/LegsProgressStatus'

export const SameGameMulti: React.FC<{
  legCount: number
  event: BetLoopKambiShareCoupon['events'][0]
  outcomes: BetLoopKambiShareCoupon['outcomes']
  betOffers: BetLoopKambiShareCoupon['betOffers']
  playedOdds: BetLoopKambiShareCoupon['couponRows'][0]['playedOdds']
  betStatus:
    | BetLoopKambiShareCoupon['bets'][0]['betStatus']
    | BetLoopKambiShareCoupon['couponRows'][0]['status']
  isWithinMultiBet?: boolean
  onMultiLegCountdownReachingZero?(): void
}> = ({
  legCount,
  event,
  outcomes,
  betOffers,
  playedOdds,
  betStatus,
  isWithinMultiBet,
  onMultiLegCountdownReachingZero,
}) => {
  const instanceId = React.useId()
  const couponDisplayContext = React.useContext(KambiHistoryCouponDisplayContext)

  const isBetLoopQuickShare = couponDisplayContext.type === 'betloop-quick-share'

  const titleText = `${legCount} Leg Same Game Multi`
  const odds = playedOdds && toMoney(formatKambiCurrencyToDollarDecimal(playedOdds))
  const sportName = event.sport
  const hasOpenOutcomes = outcomes.some(outcome => outcome.status === 'OPEN')

  return (
    <>
      <BetCardTitle
        title={titleText}
        rightComponent={
          <Flex gap={spacing.smx2}>
            {odds}

            {isBetLoopQuickShare && (
              <RadioButton
                size='md'
                type='Tick'
                id={instanceId}
                isChecked={couponDisplayContext.data.isSelected}
              />
            )}
          </Flex>
        }
        bottomLeftComponent={
          <LegsProgressStatus outcomes={outcomes} betOffers={betOffers} events={[event]} />
        }
      />

      <BetCard.EventDetails
        iconComponent={<SportsIcon sport={sportName} />}
        heading={event.eventName}
        statusComponent={
          ((hasOpenOutcomes && isWithinMultiBet) || !isWithinMultiBet) && (
            <CountdownStatus
              displayType={isWithinMultiBet ? 'multi-leg' : 'standard'}
              eventStartTime={event.eventStartDate}
              betStatus={betStatus}
              onCountdownReachingZero={onMultiLegCountdownReachingZero}
            />
          )
        }
      />

      {outcomes.map(outcome => {
        const betOffer = betOffers.find(b => b.betOfferId === outcome.betOfferId)
        if (!betOffer) return null

        const shouldProvideEventLink = outcome.status === 'OPEN' || outcome.status === 'NOT_SET'

        return (
          <BetCard.SelectionInfo
            key={outcome.betOfferId}
            // eslint-disable-next-line react/jsx-no-useless-fragment
            heading={<>{outcome.label}</>}
            subHeading={betOffer.criterion}
            linkInfo={
              shouldProvideEventLink
                ? { pathname: getEventLinkForKambiClient(event.eventId) }
                : undefined
            }
            rightAlignedComponent={<LegStatusIcon status={outcome.status} />}
            iconComponent={
              <TeamsIcon
                size='2.4rem'
                participantId={outcome.participantId ?? null}
                betOfferType={betOffer.boTypeId}
                betOfferId={outcome.betOfferId}
                outcomeId={outcome.outcomeId}
                sportName={sportName}
              />
            }
          />
        )
      })}
    </>
  )
}
